import { useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Consumer as AppContainerConsumer } from "../Main/AppContainerContext";
import { getDisplayName } from "../Utils/getDisplayName";
import { findClosestMatchingParentLocation as findClosestMatchingParentLocationUtil } from "../Utils/routing";
import { useUniqueId } from "../Utils/uniqueId";

import {
  initializeModal,
  showModal as showModalAction,
  hideModal as hideModalAction,
  removeModal as removeModalAction,
  setModalTitle as setModalTitleAction
} from "../../reducers/modals";

function withModalManager(WrappedComponent) {
  const ModalManager = props => {
    const id = useUniqueId("Modal:");

    const lastNonModalLocation = useSelector(
      ({ location }) => location.lastNonModalLocation
    );
    const currentLocation = useSelector(({ location }) => location.currentLocation);
    const isOpen = useSelector(({ modals }) => modals.activeModals[id]?.isOpen);
    const shouldBeClosed = useSelector(
      ({ modals }) => modals.activeModals[id]?.shouldBeClosed
    );
    const isModalInitialized = useSelector(({ modals }) => !!modals.activeModals[id]);

    const dispatch = useDispatch();
    const showModal = useCallback(() => dispatch(showModalAction(id)), [dispatch, id]);
    const hideModal = useCallback(() => dispatch(hideModalAction(id)), [dispatch, id]);

    const removeModal = useCallback(() => {
      dispatch(removeModalAction(id));
    }, [dispatch, id]);

    const setModalTitle = useCallback(
      title => {
        dispatch(setModalTitleAction(id, title));
      },
      [dispatch, id]
    );

    const findClosestMatchingParentLocation = useCallback(() => {
      const { pathname } = currentLocation;

      return findClosestMatchingParentLocationUtil(pathname);
    }, [currentLocation]);

    // Store the modal in the reducer's store with its id.
    useEffect(() => {
      dispatch(initializeModal(id));
    }, [dispatch, id]);

    // Set the modal as shown on mount, and set it as removed on unmount.
    useEffect(() => {
      showModal();

      return () => removeModal();
    }, [removeModal, showModal]);

    // On each component re-render, check whether the modal should be closed from its
    // input props.
    useEffect(() => {
      if (shouldBeClosed) removeModal();
    }, [removeModal, shouldBeClosed]);

    // If the modal is not initialized yet, do not render the wrapped component to ensure
    // that it gets the correct props from the ModalManager.
    if (!isModalInitialized) return;

    return (
      <AppContainerConsumer>
        {appContainer => (
          <WrappedComponent
            {...{
              ...props,
              appContainer,
              id,
              lastNonModalLocation,
              currentLocation,
              isOpen,
              showModal,
              hideModal,
              removeModal,
              setModalTitle,
              findClosestMatchingParentLocation
            }}
          />
        )}
      </AppContainerConsumer>
    );
  };

  ModalManager.displayName = `WithModalManager(${getDisplayName(WrappedComponent)})`;

  return ModalManager;
}

export default withModalManager;
